import { useCallback, useEffect, useMemo, useState } from 'react';
import unionBy from 'lodash/unionBy';

import { setIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import useTagsFilter from '@/components/FilterManager/Tags/TagsFilter/useTagsFilter';

import { SelectedTagValues, TagInputProps, TagsMenuItem } from '../../../types';
import { adaptTagsOnEdit, areRequiredTagsEmpty } from '../../../utils';
import { AttachModalProps, REQUIRED_TAGS_EMPTY_ERROR } from '../index';

type Props = Pick<
  AttachModalProps,
  'availableOn' | 'tags' | 'hasError' | 'fetchTagWithProviders' | 'subWorkspaceIds'
>;

const useAttachTags = (props: Props) => {
  const { tags, hasError, availableOn, fetchTagWithProviders, subWorkspaceIds } = props;
  const { t } = useTranslation('Tags');

  const { filterGroups, isFetching } = useTagsFilter({
    availableOn,
    requiredTag: true,
    fetchTagWithProviders,
    subWorkspaceIds,
  });

  const requiredItems = filterGroups?.[0]?.options;
  const adaptedTags = useMemo(() => adaptTagsOnEdit(tags), [tags]);
  const [tag, setTag] = useState<TagInputProps>(adaptedTags);
  const [error, setError] = useState(hasError ? t(REQUIRED_TAGS_EMPTY_ERROR) : '');

  useEffect(() => {
    setTag((prevValue) => {
      const resolvedTags = unionBy(prevValue?.tags, requiredItems, 'id');
      return setIn(prevValue || {}, 'tags', resolvedTags);
    });
  }, [requiredItems]);

  const validate = useCallback(
    (tagInput: TagInputProps) => {
      const tagsEmpty = areRequiredTagsEmpty(tagInput);
      if (tagsEmpty) {
        setError(t(REQUIRED_TAGS_EMPTY_ERROR));
        return false;
      }
      setError('');
      return true;
    },
    [t],
  );

  const onTagChange = useCallback(
    (val: TagsMenuItem[]) => {
      setTag((prevTag) => {
        const updatedTag = {
          ...prevTag,
          tags: val,
        } as TagInputProps;
        error && validate(updatedTag);
        return updatedTag;
      });
    },
    [validate, error],
  );

  const onTagValueChange = useCallback(
    (val: SelectedTagValues) => {
      setTag((prevTag) => {
        const updatedTag = {
          ...prevTag,
          tagValues: val,
        } as TagInputProps;
        error && validate(updatedTag);
        return updatedTag;
      });
    },
    [validate, error],
  );

  const onRemoveTag = useCallback(
    (tagId: string) => {
      const filteredTags = tag?.tags?.filter((tag) => tag.id !== tagId);
      filteredTags &&
        setTag((prevTag) => {
          const updatedTag = {
            ...prevTag,
            tags: filteredTags,
          } as TagInputProps;
          return updatedTag;
        });
    },
    [tag?.tags],
  );

  return { onTagChange, onTagValueChange, validate, tag, isFetching, error, onRemoveTag };
};

export default useAttachTags;
